import { type ReactNode, createContext, useContext, useEffect, useMemo, useState } from "react";

type ToolLoadingStateContextValue = {
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;
};

const ToolLoadingStateContext = createContext<ToolLoadingStateContextValue | null>(null);

export function ToolLoadingStateProvider({ children }: { children: ReactNode }) {
  const [isLoading, setIsLoading] = useState(false);
  const value = useMemo(() => ({ isLoading, setIsLoading }), [isLoading]);

  return <ToolLoadingStateContext.Provider value={value}>{children}</ToolLoadingStateContext.Provider>;
}

export function useToolLoadingState() {
  return useContext(ToolLoadingStateContext)?.isLoading ?? false;
}

// Use this inside a tool view when the real data request state lives in that view.
// It pushes the current loading flag up to the nearest ToolLoadingStateProvider so
// the overlay label can render the per-tool loading indicator.
export function useSyncToolLoadingState(isLoading: boolean) {
  const context = useContext(ToolLoadingStateContext);

  useEffect(() => {
    if (!context) {
      return;
    }

    context.setIsLoading(isLoading);

    return () => {
      context.setIsLoading(false);
    };
  }, [context, isLoading]);
}

// Convenience bridge for places where we only have a boolean in JSX and do not want
// to introduce a custom component body just to call the hook.
export function ToolLoadingStateSync({ isLoading }: { isLoading: boolean }) {
  useSyncToolLoadingState(isLoading);
  return null;
}
